import React from 'react';
import { Target, Shield, Zap, Users } from 'lucide-react';
import FeaturePage from './FeaturePage';

export default function Tactics() {
  return (
    <FeaturePage
      seo={{
        title: 'Football Tactics & Formations - Set Your Strategy | My Football Game',
        description: 'Choose from 18 formations, set your mentality, assign set-piece takers and build a tactical system that wins matches in My Football Game. Free online football tactics simulator.',
        path: '/features/tactics',
      }}
      eyebrow="Feature Spotlight"
      title="TACTICS & "
      titleAccent="FORMATIONS"
      subtitle="Choose from 18 real-world formations, set your mentality, pick set-piece takers and build a tactical system that maximises your squad's strengths."
      breadcrumbLabel="Tactics"
      sections={[
        {
          icon: <Users className="w-5 h-5" />,
          title: '18 Real-World Formations',
          content: (
            <>
              <p>My Football Game includes 18 formations used by real football managers: 4-4-2, 4-3-3, 3-5-2, 4-2-3-1, 5-3-2, and many more. Each formation changes how your players are positioned and how they interact during the match simulation.</p>
              <p>Every position has detailed roles — not just "midfielder" but CAM, CDM, CM, LM, RM — with 26 distinct positions in total. Placing a player in their natural position boosts their performance through the position familiarity system.</p>
            </>
          ),
        },
        {
          icon: <Target className="w-5 h-5" />,
          title: 'Team Mentality',
          content: (
            <>
              <p>Set your team's overall mentality on a five-step scale: defensive, cautious, balanced, positive or attacking. Then fine-tune the defensive line, width, passing style, tempo, transitions and what happens after losing the ball (drop back or gegenpress).</p>
              <p>A balanced mentality works well against most opponents, but switching to attacking when chasing a game — or parking the bus to defend a lead — can be the difference between winning and losing.</p>
            </>
          ),
        },
        {
          icon: <Zap className="w-5 h-5" />,
          title: 'Set Pieces & Takers',
          content: (
            <>
              <p>Assign your best players as free-kick, corner, and penalty takers. The match engine uses these assignments directly — a player with high free-kick accuracy taking set pieces will create more chances than a random assignment.</p>
              <p>Set pieces are responsible for a significant percentage of goals in My Football Game, just like real football. Getting your takers right is a quick tactical win that many managers overlook.</p>
            </>
          ),
        },
        {
          icon: <Shield className="w-5 h-5" />,
          title: 'Tactics Feed the Match Engine',
          content: (
            <>
              <p>Your tactical choices are not cosmetic — they directly influence the match simulation engine. Formation shape affects passing lanes, mentality changes shot frequency, and pressing intensity determines how quickly you win the ball back.</p>
              <p>The best managers adapt their tactics to each opponent. Playing a high-pressing 4-3-3 against a team that struggles under pressure is very different from sitting deep in a 5-4-1 against a stronger squad.</p>
            </>
          ),
        },
      ]}
      faqItems={[
        { q: 'How many formations are available?', a: 'My Football Game offers 18 formations including 4-4-2, 4-3-3, 3-5-2, 4-2-3-1, 4-1-4-1, 5-3-2, and more. Each formation uses the 26-position system for detailed player placement.' },
        { q: 'Do tactics really affect match results?', a: 'Yes. Your formation, mentality, pressing style, and set-piece takers all feed directly into the match simulation engine. A well-matched tactic can beat a stronger team on paper.' },
        { q: 'What is position familiarity?', a: 'Players perform best in their natural position. A striker played as a midfielder will underperform. Check each player\'s position tags and assign them where they are most comfortable.' },
        { q: 'Can I change tactics between matches?', a: 'Yes, you can change your formation, lineup, mentality, and set-piece takers before every match. Adapting to each opponent is a key part of successful management.' },
      ]}
      ctaTitle="Outsmart your opponents"
      ctaText="Create your club, set your tactics and prove your tactical genius. Free to play, no download needed."
    />
  );
}
